import { keyBy } from "lodash";
import { useMemo } from "react";

import type { CustomerCustomsStatus } from "../../gql/graphql";
import type {
  CreateCandidate,
  CreateFormState,
} from "../../services/create/createFormReducer";
import { useCreateFormState } from "../../services/create/hooks/useCreateFormState";
import type { BookingFormState } from "../../services/update-form/updateFormReducer";
import type { GoodsItems } from "../goods/goodsUtils";
import type {
  BasicBooking,
  BasicBooking_sailing_route_exportSettings,
  BasicBooking_sailing_route_importSettings,
} from "../graphql/fragments/gql/BasicBooking";
import type { Route_ukCustomsClassifications } from "../graphql/fragments/gql/Route";
import { customsClassificationDropdownEmptyOption } from "../graphql/useCustomsClassificationsOptions";
import { useBookingFormInstanceId } from "../update-information/BookingFormInstanceIdProvider";
import { useUpdateFormState } from "../update-information/hooks/useUpdateFormState";

export const MINIMAL_BOOKING_SYMBOL = Symbol("MINIMAL_BOOKING_SYMBOL");

// TODO Move other minimal booking related utils to their use-cases

export interface MinimalBooking {
  bookingNo?: number;
  customerCustomsStatus?: CustomerCustomsStatus;
  customsClassificationCode?: string;
  departureDate?: string;
  departureTime?: string;
  goods?: GoodsItems;
  minimalBooking: typeof MINIMAL_BOOKING_SYMBOL;
  noOfDrivers?: number;
  noOfPlugins?: number;
  route?: {
    code: string;
    useCargoWeight: boolean;
    useCustomsClassification: boolean;
    useHazardousGoods: boolean;
    useLivestockType: boolean;
    useSenderReceiverCountry: boolean;
    importSettings: BasicBooking_sailing_route_importSettings | null;
    exportSettings: BasicBooking_sailing_route_exportSettings | null;
    ukCustomsClassifications: Route_ukCustomsClassifications[];
  };
  showDaysOnQuay?: boolean;
  vehicleType?: {
    useAnimals: boolean;
    useCargoWeight: boolean;
    useHazardous: boolean;
    useNoOfDrivers: boolean;
    useTemperature: boolean;
    useTradeWeight: boolean;
    useTrailerReg: boolean;
    useVehicleReg: boolean;
  };
}

export const transformUpdateBookingFormStateToMinimalBooking = (
  booking: BookingFormState,
): MinimalBooking => {
  const {
    route,
    vehicleType,
    showDaysOnQuay,
    noOfPlugins,
    sailing,
    departureDate,
    customsClassification,
    bookingNo,
    goods,
  } = booking;

  return {
    minimalBooking: MINIMAL_BOOKING_SYMBOL,
    noOfDrivers: booking.noOfDrivers,
    bookingNo,
    showDaysOnQuay,
    noOfPlugins,
    route,
    vehicleType,
    goods,
    departureDate,
    departureTime: sailing?.departureTime,
    customsClassificationCode:
      customsClassification === customsClassificationDropdownEmptyOption.data
        ? undefined
        : customsClassification?.value,
  };
};

export const transformCreateBookingFormStateToMinimalBooking = (
  formState: CreateFormState,
  createCandidate: CreateCandidate,
): MinimalBooking => {
  const { route, sailing, departureDate } = formState;
  const {
    vehicleType,
    numberOfDrivers,
    hasElectricalPlugin,
    customsClassification,
    goods,
  } = createCandidate;
  return {
    minimalBooking: MINIMAL_BOOKING_SYMBOL,
    noOfDrivers: numberOfDrivers,
    showDaysOnQuay: false,
    noOfPlugins: hasElectricalPlugin ? 1 : 0,
    route: route ?? undefined,
    vehicleType: vehicleType ?? undefined,
    goods,
    departureDate,
    departureTime: sailing?.departureTime,
    customsClassificationCode:
      customsClassification === customsClassificationDropdownEmptyOption.data
        ? undefined
        : customsClassification?.value,
  };
};

export const transformBookingToMinimalBooking = (
  booking: BasicBooking,
): MinimalBooking => {
  const {
    sailing,
    vehicleType,
    bookingNo,
    customerCustomsStatus,
    showDaysOnQuay,
    noOfPlugins,
    noOfDrivers,
    customsClassification,
    goods,
  } = booking;

  return {
    minimalBooking: MINIMAL_BOOKING_SYMBOL,
    bookingNo,
    showDaysOnQuay,
    customerCustomsStatus,
    noOfPlugins,
    noOfDrivers,
    route: sailing.route,
    vehicleType,
    goods: keyBy(goods, (g) => g.id),
    departureDate: sailing.departureDate,
    departureTime: sailing.departureTime,
    customsClassificationCode:
      customsClassification === customsClassificationDropdownEmptyOption.data
        ? undefined
        : customsClassification?.value,
  };
};

export const useUpdateFormStateMinimalBooking = ():
  | MinimalBooking
  | undefined => {
  const { formState } = useUpdateFormState(useBookingFormInstanceId());
  return useMemo(() => {
    if (!formState) {
      return undefined;
    }
    return transformUpdateBookingFormStateToMinimalBooking(formState);
  }, [formState]);
};

export const useCreateFormStateMinimalBooking = (
  index: number,
): MinimalBooking | undefined => {
  const { formState } = useCreateFormState();
  return useMemo(() => {
    const createCandidate = formState.bookings[index];
    if (!createCandidate) {
      return undefined;
    }
    return transformCreateBookingFormStateToMinimalBooking(
      formState,
      createCandidate,
    );
  }, [formState, index]);
};
